import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { axiosInstance } from '../../App';
import {
  Users,
  UserPlus,
  FileText,
  CheckCircle,
  AlertTriangle,
  Briefcase,
  DollarSign,
  Receipt,
  Clock,
  Package,
  UserCog,
  TrendingUp,
  TrendingDown,
  ArrowRight
} from 'lucide-react';

export default function EnhancedDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMetric, setSelectedMetric] = useState(null);
  const [detailData, setDetailData] = useState([]);
  const [showDetailDialog, setShowDetailDialog] = useState(false);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axiosInstance.get('/dashboard/stats');
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to load dashboard stats');
    } finally {
      setLoading(false);
    }
  };

  const handleCardClick = async (metric) => {
    setSelectedMetric(metric);
    setShowDetailDialog(true);
    
    try {
      let data = [];
      switch(metric) {
        case 'customers':
          const customers = await axiosInstance.get('/customers');
          data = customers.data;
          break;
        case 'leads':
          const leads = await axiosInstance.get('/leads');
          data = leads.data;
          break;
        case 'quotes':
          const quotes = await axiosInstance.get('/quotes');
          data = quotes.data;
          break;
        case 'jobs':
          const jobs = await axiosInstance.get('/jobs');
          data = jobs.data;
          break;
        case 'invoices':
          const invoices = await axiosInstance.get('/invoices');
          data = invoices.data;
          break;
        default:
          data = [];
      }
      setDetailData(data);
    } catch (error) {
      toast.error('Failed to load details');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#595959]"></div>
      </div>
    );
  }

  const MetricCard = ({ title, icon: Icon, data, onClick, gradient }) => (
    <Card 
      className="backdrop-blur-sm bg-white/90 shadow-lg border-0 hover:shadow-2xl transition-all cursor-pointer transform hover:-translate-y-1"
      onClick={onClick}
      data-testid={`metric-card-${title.toLowerCase().replace(/\s/g, '-')}`}
    >
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold text-gray-700">{title}</CardTitle>
          <div className={`p-3 rounded-full ${gradient}`}>
            <Icon className="text-white" size={24} />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {Object.entries(data).map(([key, value]) => (
          <div key={key} className="flex items-center justify-between">
            <span className="text-sm text-gray-600 capitalize">
              {key.replace(/_/g, ' ')}:
            </span>
            <span className="text-lg font-bold text-gray-900">
              {typeof value === 'number' && key.includes('amount') 
                ? `$${(value || 0).toFixed(2)}` 
                : (value || 0)}
            </span>
          </div>
        ))}
        <div className="pt-2 flex items-center text-[#595959] text-sm font-medium">
          View Details <ArrowRight size={16} className="ml-1" />
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div data-testid="enhanced-dashboard">
      <div className="mb-8">
        <h2 className="text-4xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          Business Intelligence Dashboard
        </h2>
        <p className="text-gray-600">Real-time metrics and insights at a glance</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {/* 1. Customers */}
        <MetricCard
          title="Total Customers"
          icon={Users}
          gradient="bg-gradient-to-br from-blue-500 to-blue-600"
          data={{
            total: stats.customers.total,
            'new (30 days)': stats.customers.new_this_month,
            existing: stats.customers.existing
          }}
          onClick={() => handleCardClick('customers')}
        />

        {/* 2. Leads */}
        <MetricCard
          title="Leads Pipeline"
          icon={UserPlus}
          gradient="bg-gradient-to-br from-purple-500 to-purple-600"
          data={{
            total: stats.leads.total,
            new: stats.leads.new,
            assessments: stats.leads.assessments_completed,
            overdue: stats.leads.overdue
          }}
          onClick={() => handleCardClick('leads')}
        />

        {/* 3. Quotes */}
        <MetricCard
          title="Quotes Status"
          icon={FileText}
          gradient="bg-gradient-to-br from-yellow-500 to-yellow-600"
          data={{
            total: stats.quotes.total,
            approved: stats.quotes.approved,
            draft: stats.quotes.draft,
            sent: stats.quotes.sent,
            converted: stats.quotes.converted,
            'changes requested': stats.quotes.changes_requested
          }}
          onClick={() => handleCardClick('quotes')}
        />

        {/* 4. Jobs */}
        <MetricCard
          title="Jobs Overview"
          icon={Briefcase}
          gradient="bg-gradient-to-br from-teal-500 to-teal-600"
          data={{
            total: stats.jobs.total,
            active: stats.jobs.active,
            'active amount': stats.jobs.active_amount,
            completed: stats.jobs.completed,
            'yet to start': stats.jobs.yet_to_start
          }}
          onClick={() => handleCardClick('jobs')}
        />

        {/* 5. Invoices */}
        <MetricCard
          title="Invoices"
          icon={Receipt}
          gradient="bg-gradient-to-br from-green-500 to-green-600"
          data={{
            'raised count': stats.invoices.raised_count,
            'raised amount': stats.invoices.raised_amount,
            'paid count': stats.invoices.paid_count,
            'paid amount': stats.invoices.paid_amount,
            'yet to raise': stats.invoices.yet_to_raise
          }}
          onClick={() => handleCardClick('invoices')}
        />

        {/* 6. Receivables */}
        <Card className="backdrop-blur-sm bg-white/90 shadow-lg border-0">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold text-gray-700">Receivables</CardTitle>
              <div className="p-3 rounded-full bg-gradient-to-br from-rose-500 to-rose-600">
                <DollarSign className="text-white" size={24} />
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Total:</span>
              <span className="text-lg font-bold text-gray-900">
                ${(stats.receivables?.total || 0).toFixed(2)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Due &gt; 14 Days:</span>
              <span className="text-lg font-bold text-rose-600">
                ${(stats.receivables?.overdue_14_days || stats.receivables?.overdue || 0).toFixed(2)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Overdue Count:</span>
              <Badge className="bg-rose-500">{stats.receivables?.overdue_count || 0}</Badge>
            </div>
          </CardContent>
        </Card>

        {/* 7. Packages */}
        <Card className="backdrop-blur-sm bg-white/90 shadow-lg border-0">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold text-gray-700">Packages</CardTitle>
              <div className="p-3 rounded-full bg-gradient-to-br from-indigo-500 to-indigo-600">
                <Package className="text-white" size={24} />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">Active Subscriptions:</span>
              <span className="text-3xl font-bold text-gray-900">
                {stats.packages?.subscribed || 0}
              </span>
            </div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">Total Customers:</span>
              <span className="text-xl font-bold text-gray-900">
                {stats.packages?.total_customers || 0}
              </span>
            </div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">Total Hours:</span>
              <span className="text-lg font-semibold text-gray-800">
                {stats.packages?.total_hours || 0}h
              </span>
            </div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">Used Hours:</span>
              <span className="text-lg font-semibold text-orange-600">
                {stats.packages?.used_hours || 0}h
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Remaining Hours:</span>
              <span className="text-lg font-semibold text-green-600">
                {stats.packages?.remaining_hours || 0}h
              </span>
            </div>
          </CardContent>
        </Card>

        {/* 8. Manpower */}
        <Card className="backdrop-blur-sm bg-white/90 shadow-lg border-0 md:col-span-2 lg:col-span-1">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold text-gray-700">Manpower</CardTitle>
              <div className="p-3 rounded-full bg-gradient-to-br from-orange-500 to-orange-600">
                <UserCog className="text-white" size={24} />
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-green-50 rounded-lg">
                <p className="text-xs text-gray-600 mb-1">Today Engaged</p>
                <p className="text-2xl font-bold text-green-600">{stats.manpower.today_engaged}</p>
              </div>
              <div className="p-3 bg-gray-100 rounded-lg">
                <p className="text-xs text-gray-600 mb-1">On Bench</p>
                <p className="text-2xl font-bold text-gray-600">{stats.manpower.today_on_bench}</p>
              </div>
            </div>
            <div className="pt-2 border-t space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Yesterday Hours:</span>
                <span className="font-bold text-gray-900">{(stats.manpower?.yesterday_hours || 0).toFixed(1)}h</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Yesterday Bench:</span>
                <span className="font-bold text-gray-900">{stats.manpower?.yesterday_on_bench || 0}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Performance Indicators */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
        <Card className="backdrop-blur-sm bg-gradient-to-br from-teal-500 to-teal-600 text-white border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="text-white">Revenue Performance</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-teal-100 text-sm">Total Revenue</p>
                <p className="text-4xl font-bold">${(stats.invoices?.paid_amount || 0).toFixed(0)}</p>
              </div>
              <TrendingUp size={48} className="opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card className="backdrop-blur-sm bg-gradient-to-br from-orange-500 to-orange-600 text-white border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="text-white">Completion Rate</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-100 text-sm">Jobs Completed</p>
                <p className="text-4xl font-bold">
                  {stats.jobs.total > 0 
                    ? Math.round((stats.jobs.completed / stats.jobs.total) * 100)
                    : 0}%
                </p>
              </div>
              <CheckCircle size={48} className="opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card className="backdrop-blur-sm bg-gradient-to-br from-purple-500 to-purple-600 text-white border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="text-white">Active Pipeline</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm">Leads + Jobs</p>
                <p className="text-4xl font-bold">{(stats.leads?.total || 0) + (stats.jobs?.active || 0)}</p>
              </div>
              <Clock size={48} className="opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl">
              {selectedMetric?.charAt(0).toUpperCase() + selectedMetric?.slice(1)} Details
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {detailData.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-100">
                    <tr>
                      {Object.keys(detailData[0]).slice(0, 6).map((key) => (
                        <th key={key} className="px-4 py-2 text-left text-sm font-medium text-gray-700">
                          {key.replace(/_/g, ' ').toUpperCase()}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {detailData.slice(0, 20).map((item, index) => (
                      <tr key={index} className="border-b hover:bg-gray-50">
                        {Object.values(item).slice(0, 6).map((value, i) => (
                          <td key={i} className="px-4 py-3 text-sm text-gray-600">
                            {typeof value === 'object' ? JSON.stringify(value).slice(0, 30) : String(value).slice(0, 50)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                No data available
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
